import { ImageX } from '@bridebook/ui';
import {
  Palette,
  PALETTE_ACCENTS,
  SKETCHES_ACCENTS,
} from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/palette-config';
import { useThemeContext } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/theme-provider';

/**
 * This component uses absolute positioning with full height coverage and contains a sticky element
 * with screen height to mimic "fixed" positioning behavior. We avoid using position: fixed directly
 * as it would not work properly within panel and preview contexts.
 */

export const OverAccent = () => {
  const { palette } = useThemeContext();

  if (palette === Palette.Bridebook) return null;

  return (
    <div
      className="pointer-events-none absolute inset-0 z-10 hidden @screen-lg:block"
      key={palette}>
      <div className="sticky top-0 h-[var(--desktop-height,100vh)] w-full">
        <OverAccentElement />
      </div>
    </div>
  );
};

const OverAccentElement = () => {
  const { palette } = useThemeContext();

  switch (palette) {
    case Palette.Roses:
    case Palette.RosesPlum:
    case Palette.RosesBrown:
      return (
        <>
          <div className="absolute top-0 left-0 h-[446px] w-[270px]">
            <ImageX w={270} h={446} src={PALETTE_ACCENTS[palette]} fit="cover" />
          </div>
          <div className="absolute right-0 bottom-0 h-[446px] w-[270px] scale-x-[-1] scale-y-[-1]">
            <ImageX w={270} h={446} src={PALETTE_ACCENTS[palette]} />
          </div>
        </>
      );
    case Palette.Foliage:
    case Palette.FoliageOrange:
    case Palette.FoliageBlue:
      return (
        <CenteredWrapper>
          <div className="absolute top-[127px] left-[-130px] h-[348px] w-[188px]">
            <ImageX w={188} h={348} src={PALETTE_ACCENTS[palette]} fit="cover" />
          </div>
          <div className="absolute top-[127px] right-[-130px] h-[348px] w-[188px] scale-x-[-1]">
            <ImageX w={188} h={348} src={PALETTE_ACCENTS[palette]} fit="cover" />
          </div>
        </CenteredWrapper>
      );
    case Palette.Whimsical:
    case Palette.WhimsicalBeige:
    case Palette.WhimsicalGreen:
      return (
        <CenteredWrapper>
          <div className="absolute bottom-[80px] left-[-130px] h-[340px] w-[240px]">
            <ImageX w={240} h={340} src={PALETTE_ACCENTS[palette]} fit="cover" />
          </div>
          <div className="absolute right-[-130px] bottom-[80px] h-[340px] w-[240px] scale-x-[-1]">
            <ImageX w={240} h={340} src={PALETTE_ACCENTS[palette]} fit="cover" />
          </div>
        </CenteredWrapper>
      );
    case Palette.Sketches:
    case Palette.SketchesGreen:
    case Palette.SketchesLilac:
      return (
        <>
          <CenteredWrapper>
            <div className="absolute top-[50px] left-[-144px] h-[772px] w-[201px]">
              <ImageX w={201} h={772} src={SKETCHES_ACCENTS[palette].desktopLeft} fit="cover" />
            </div>
            <div className="absolute top-[100px] right-[-170px] h-[774px] w-[232px]">
              <ImageX w={232} h={774} src={SKETCHES_ACCENTS[palette].desktopRight} fit="cover" />
            </div>
          </CenteredWrapper>
        </>
      );
    default:
      return null;
  }
};

const CenteredWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="absolute top-0 right-0 left-0 h-[var(--desktop-height,100vh)] px-[170px]">
    <div className="relative mx-auto h-full w-full max-w-[1100px]">{children}</div>
  </div>
);
